import type { TClinicDetail, TClinicService, TClinicServiceCategory, TMetroStation } from '~/types/clinic'
import { getPlural } from '~/utils/helpers'

const VET_NAME_RE = /вет(еринар|клиник|центр)/i
const CONSULTATION_RE = /при[её]м|консультац/i
const DAY_ABBR = 'пн|вт|ср|чт|пт|сб|вс'

export function toE164Phone(phone: string | null | undefined): string | null {
  if (!phone) {
    return null
  }
  const digits = phone.replace(/\D+/g, '')
  if (!digits) {
    return null
  }
  if (digits.length === 11 && digits.startsWith('8')) {
    return `+7${digits.slice(1)}`
  }
  if (digits.length === 11 && digits.startsWith('7')) {
    return `+${digits}`
  }
  if (digits.length === 10) {
    return `+7${digits}`
  }
  if (phone.trim().startsWith('+') && digits.length >= 11) {
    return `+${digits}`
  }
  return null
}

function nearestMetro(stations: TMetroStation[] | undefined): TMetroStation | null {
  if (!stations?.length) {
    return null
  }
  return [...stations].sort((a, b) => (a.distanceKm ?? 99) - (b.distanceKm ?? 99))[0] ?? null
}

function metroLabel(metro: TMetroStation): string {
  const line = metro.line?.name || ''
  const prefix = /мцд|мцк/i.test(line) || /мцд|мцк/i.test(metro.name) ? 'ст.' : 'м.'
  return `${prefix} ${metro.name}`
}

function formatDistrictName(name: string): string {
  if (/район|округ|поселен/i.test(name)) {
    return name
  }
  return `район ${name}`
}

function isAlwaysOpenClinic(clinic: TClinicDetail): boolean {
  return Boolean(
    clinic.tags?.some((tag) => /круглосуточ/i.test(tag))
    || /круглосуточ/i.test(clinic.schedule || ''),
  )
}

function locationPart(clinic: TClinicDetail): string | null {
  const metro = nearestMetro(clinic.metroStations)
  if (metro && (metro.distanceKm == null || metro.distanceKm <= 2)) {
    return metroLabel(metro)
  }
  if (clinic.districtName) {
    return formatDistrictName(clinic.districtName)
  }
  return null
}

function typePart(clinic: TClinicDetail): string | null {
  const nameHasVet = VET_NAME_RE.test(clinic.name)
  const alwaysOpen = isAlwaysOpenClinic(clinic)
  if (alwaysOpen) {
    return nameHasVet ? 'круглосуточно' : 'круглосуточная ветклиника'
  }
  if (nameHasVet) {
    return null
  }
  return 'ветклиника'
}

function flattenServices(clinic: TClinicDetail): TClinicService[] {
  if (!clinic.services?.length) {
    return []
  }
  const first = clinic.services[0] as TClinicServiceCategory | TClinicService
  if ('categoryName' in first && 'services' in first) {
    return (clinic.services as TClinicServiceCategory[]).flatMap((cat) => cat.services || [])
  }
  return clinic.services as TClinicService[]
}

function servicesCount(clinic: TClinicDetail): number {
  return flattenServices(clinic).length
}

function hasPricelist(clinic: TClinicDetail): boolean {
  return servicesCount(clinic) > 0
}

function formatRub(amount: number): string {
  return `${Math.round(amount).toLocaleString('ru-RU')} ₽`
}

function minConsultationPrice(clinic: TClinicDetail): number | null {
  const prices = flattenServices(clinic)
    .filter((service) => CONSULTATION_RE.test(service.name) && service.price != null && service.price > 0)
    .map((service) => service.price as number)
  if (!prices.length) {
    return null
  }
  return Math.min(...prices)
}

function tailPart(clinic: TClinicDetail): string {
  const bits: string[] = []
  if (hasPricelist(clinic)) {
    bits.push('цены')
  }
  if ((clinic.reviewCount ?? 0) > 0) {
    bits.push('отзывы')
  }
  if (clinic.canBookAppointment) {
    bits.push('запись')
  }
  if (!bits.length) {
    return 'адрес, телефон, режим работы'
  }
  return bits.join(', ')
}

export function buildClinicSeoTitle(clinic: TClinicDetail, cityName: string): string {
  const chunks: string[] = []
  const type = typePart(clinic)
  const location = locationPart(clinic)
  if (type) {
    chunks.push(type)
  }
  if (location) {
    chunks.push(location)
  }
  if (cityName) {
    chunks.push(cityName)
  }
  return `${clinic.name} — ${chunks.join(', ')}: ${tailPart(clinic)}`
}

/**
 * Нормализация графика для description.
 * Одиночный день («Пн 10:00-22:00») — ошибка данных, не выводим.
 */
function compactClock(hour: string, minute: string): string {
  return `${Number(hour)}:${minute}`
}

function firstTimeRange(raw: string): { open: string, close: string } | null {
  const fromTo = raw.match(/с\s+(\d{1,2})[:.](\d{2})\s+до\s+(\d{1,2})[:.](\d{2})/i)
  if (fromTo) {
    return { open: compactClock(fromTo[1], fromTo[2]), close: compactClock(fromTo[3], fromTo[4]) }
  }
  const dash = raw.match(/(\d{1,2})[:.](\d{2})\s*[-–—]\s*(\d{1,2})[:.](\d{2})/)
  if (dash) {
    return { open: compactClock(dash[1], dash[2]), close: compactClock(dash[3], dash[4]) }
  }
  return null
}

export function formatScheduleForDescription(
  schedule: string | null | undefined,
  isAlwaysOpen?: boolean,
): string | null {
  if (isAlwaysOpen || /круглосуточ/i.test(schedule || '')) {
    return 'Круглосуточно'
  }
  const raw = (schedule || '').replace(/\s+/g, ' ').trim()
  if (!raw) {
    return null
  }

  const hasDayRange = new RegExp(`(?:${DAY_ABBR})\\s*[-–—]\\s*(?:${DAY_ABBR})`, 'i').test(raw)
  const startsWithSingleDay = new RegExp(`^(?:${DAY_ABBR})(?:\\s|,)`, 'i').test(raw)
  if (startsWithSingleDay && !hasDayRange && !/ежедневн|будни|выходн/i.test(raw)) {
    return null
  }

  const weekday = raw.match(/будни[^0-9]*(?:с\s+)?(\d{1,2})[:.](\d{2})\s*(?:[-–—]|до)\s*(\d{1,2})[:.](\d{2})/i)
  const weekend = raw.match(/выходн[^0-9]*(?:с\s+)?(\d{1,2})[:.](\d{2})\s*(?:[-–—]|до)\s*(\d{1,2})[:.](\d{2})/i)
  if (weekday && weekend) {
    return `Будни ${compactClock(weekday[1], weekday[2])}–${compactClock(weekday[3], weekday[4])}, Выходные ${compactClock(weekend[1], weekend[2])}–${compactClock(weekend[3], weekend[4])}`
  }

  const times = firstTimeRange(raw)

  if (times && (/пн\s*[-–—]\s*вс/i.test(raw) || /ежедневн/i.test(raw))) {
    return `Ежедневно ${times.open}–${times.close}`
  }
  if (times && /пн\s*[-–—]\s*пт/i.test(raw) && !/сб|вс/i.test(raw.replace(/пн\s*[-–—]\s*пт/i, ''))) {
    return `Пн–Пт ${times.open}–${times.close}`
  }
  if (times && /будни/i.test(raw) && !/выходн/i.test(raw)) {
    return `Будни ${times.open}–${times.close}`
  }

  return raw.replace(/[-–—]/g, '–')
}

function pricesBit(clinic: TClinicDetail): string {
  const count = servicesCount(clinic)
  if (count <= 0) {
    return '—'
  }
  const consultation = minConsultationPrice(clinic)
  if (consultation != null) {
    return `Приём от ${formatRub(consultation)}, цены на ${count} услуг`
  }
  return `Цены на ${count} услуг`
}

function ratingBit(clinic: TClinicDetail): string {
  const reviewCount = clinic.reviewCount ?? 0
  if (reviewCount <= 0 || !clinic.rating) {
    return 'Отзывов пока нет'
  }
  const rating = String(clinic.rating).replace('.', ',')
  const word = getPlural(reviewCount, ['отзыву', 'отзывам', 'отзывам'])
  return `Рейтинг ${rating} по ${reviewCount} ${word}`
}

export function buildClinicSeoDescription(clinic: TClinicDetail, cityPrepositional: string): string {
  const metro = nearestMetro(clinic.metroStations)
  const metroBit = metro && (metro.distanceKm == null || metro.distanceKm <= 2)
    ? `, ${metroLabel(metro)}${metro.distanceKm != null ? ` (${Math.round(metro.distanceKm * 1000)} м)` : ''}`
    : ''
  const schedule = formatScheduleForDescription(clinic.schedule, isAlwaysOpenClinic(clinic))
  const bookingBit = clinic.canBookAppointment ? 'Онлайн-запись на Vetsy' : 'Телефон и запись на Vetsy'
  const prep = cityPrepositional.startsWith('в ') ? cityPrepositional : `в ${cityPrepositional}`

  return [
    `${clinic.name} — ветклиника ${prep}: ${clinic.address || 'адрес'}${metroBit}.`,
    schedule ? `${schedule}.` : '',
    `${ratingBit(clinic)}.`,
    `${pricesBit(clinic)}.`,
    `${bookingBit}.`,
  ].filter(Boolean).join(' ')
}

export type OpeningHoursSpec = {
  '@type': 'OpeningHoursSpecification'
  dayOfWeek: string[]
  opens: string
  closes: string
}

const ALL_DAYS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
]

function padClock(time: string): string {
  return time.padStart(5, '0')
}

function parseLineHours(line: string): [string, string] | null {
  const fromTo = line.match(/с\s+(\d{1,2}:\d{2})\s+до\s+(\d{1,2}:\d{2})/i)
  if (fromTo) {
    return [padClock(fromTo[1]), padClock(fromTo[2])]
  }
  const dash = line.match(/(\d{1,2}:\d{2})\s*[-–—]\s*(\d{1,2}:\d{2})/)
  if (dash) {
    return [padClock(dash[1]), padClock(dash[2])]
  }
  return null
}

function daysForScheduleLine(line: string): string[] {
  if (/будни/i.test(line) || /пн\s*[-–—]\s*пт/i.test(line)) {
    return ALL_DAYS.slice(0, 5)
  }
  if (/выходн/i.test(line) || /сб\s*[-–—]\s*вс/i.test(line)) {
    return ALL_DAYS.slice(5)
  }
  return ALL_DAYS
}

export function buildOpeningHoursSpecification(
  schedule: string | null | undefined,
  isAlwaysOpen?: boolean,
): OpeningHoursSpec[] {
  if (isAlwaysOpen || /круглосуточ/i.test(schedule || '')) {
    return [{
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ALL_DAYS,
      opens: '00:00',
      closes: '23:59',
    }]
  }

  const lines = (schedule || '').split(/\n|<br\s*\/?>/i).map((line) => line.trim()).filter(Boolean)
  const specs: OpeningHoursSpec[] = []

  for (const line of lines) {
    const range = parseLineHours(line)
    if (!range) {
      continue
    }
    specs.push({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: daysForScheduleLine(line),
      opens: range[0],
      closes: range[1],
    })
  }

  return specs
}

export function streetAddressWithoutDistrict(address: string | null | undefined): string | null {
  if (!address) {
    return null
  }
  return address
    .replace(/^р-н\s+[^,]+,\s*/i, '')
    .replace(/^район\s+[^,]+,\s*/i, '')
    .trim() || null
}
